import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createCoinIcon } from '../render/coin';
import { futureValueSeries, rule72 } from '../finance/annuity';
import { type Series } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface PowerOfTimeState {
  monthly: number; // shared contribution amount
  rate: number; // annual nominal rate, decimal
  horizon: number; // total years on the x-axis
  aStart: number; // saver A: contribution window start year
  aStop: number; // saver A: contribution window stop year
  bStart: number; // saver B: contribution window start year
  bStop: number; // saver B: contribution window stop year
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

/**
 * Build the model for the power-of-time experiment.
 * @param state      Experiment state.
 * @param advanced   When true, append doubling and cost-of-waiting rows.
 */
export function powerOfTimeModel(
  state: PowerOfTimeState,
  advanced = false,
): { series: Series[]; readout: ReadoutRow[] } {
  const { monthly, rate, horizon, aStart, aStop, bStart, bStop } = state;

  // Build series, relabelling away from the default 'fv' id.
  const saverA: Series = {
    ...futureValueSeries(monthly, rate, 12, horizon, {
      startYear: aStart,
      stopYear: aStop,
    }),
    id: 'saverA',
  };

  const saverB: Series = {
    ...futureValueSeries(monthly, rate, 12, horizon, {
      startYear: bStart,
      stopYear: bStop,
    }),
    id: 'saverB',
  };

  const aFinal = saverA.points.at(-1)?.value ?? 0;
  const bFinal = saverB.points.at(-1)?.value ?? 0;
  const aContributed = monthly * 12 * (aStop - aStart);
  const bContributed = monthly * 12 * (bStop - bStart);

  const readout: ReadoutRow[] = [
    {
      id: 'a-contributed',
      lhs: 'stat.a-contributed.label',
      relation: '=',
      rhs: formatMoney(aContributed),
    },
    {
      id: 'a-final',
      lhs: 'stat.a-final.label',
      relation: '=',
      rhs: formatMoney(aFinal),
      emphasis: aFinal >= bFinal ? 'invariant' : 'neutral',
    },
    {
      id: 'b-contributed',
      lhs: 'stat.b-contributed.label',
      relation: '=',
      rhs: formatMoney(bContributed),
    },
    {
      id: 'b-final',
      lhs: 'stat.b-final.label',
      relation: '=',
      rhs: formatMoney(bFinal),
      emphasis: bFinal > aFinal ? 'invariant' : 'neutral',
    },
  ];

  if (advanced) {
    readout.push({
      id: 'doubling',
      lhs: 'stat.doubling.label',
      relation: '≈',
      rhs: formatYears(rule72(rate)),
    });
    const shortfall = Math.max(0, aFinal - bFinal);
    readout.push({
      id: 'cost-of-waiting',
      lhs: 'stat.cost-of-waiting.label',
      relation: '=',
      rhs: formatMoney(shortfall),
    });
  }

  return { series: [saverA, saverB], readout };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(_advanced: boolean): PowerOfTimeState {
  return {
    monthly: 200,
    rate: 0.08,
    horizon: 40,
    aStart: 0,
    aStop: 10,
    bStart: 10,
    bStop: 40,
  };
}

const PRESETS = [
  {
    id: 'classic-early-vs-late',
    labelKey: 'preset.classic-early-vs-late.label' as FinanceKey,
  },
  { id: 'same-total', labelKey: 'preset.same-total.label' as FinanceKey },
  { id: 'late-bloomer', labelKey: 'preset.late-bloomer.label' as FinanceKey },
];

function applyPresetLocal(state: PowerOfTimeState, id: string): PowerOfTimeState {
  switch (id) {
    case 'classic-early-vs-late':
      // A contributes 10 years early, B contributes 30 years from year 10
      return {
        ...state,
        monthly: 200,
        rate: 0.08,
        horizon: 40,
        aStart: 0,
        aStop: 10,
        bStart: 10,
        bStop: 40,
      };
    case 'same-total':
      // Both contribute same total amount (10 vs 10 years), A starts earlier
      return {
        ...state,
        monthly: 200,
        rate: 0.07,
        horizon: 40,
        aStart: 0,
        aStop: 10,
        bStart: 30,
        bStop: 40,
      };
    case 'late-bloomer':
      // B contributes much more monthly to try to catch up
      return {
        ...state,
        monthly: 150,
        rate: 0.06,
        horizon: 35,
        aStart: 0,
        aStop: 8,
        bStart: 15,
        bStop: 35,
      };
    default:
      return state;
  }
}

function randomise(state: PowerOfTimeState, prng: (max?: number) => number): PowerOfTimeState {
  const monthlyOptions = [100, 150, 200, 300, 500];
  const rateOptions = [0.05, 0.06, 0.07, 0.08, 0.09, 0.1];
  const horizonOptions = [30, 35, 40, 45];
  const aStopOptions = [5, 8, 10, 12, 15];

  const monthly = monthlyOptions[Math.floor(prng() * monthlyOptions.length)] ?? 200;
  const rate = rateOptions[Math.floor(prng() * rateOptions.length)] ?? 0.08;
  const horizon = horizonOptions[Math.floor(prng() * horizonOptions.length)] ?? 40;
  const aStop = aStopOptions[Math.floor(prng() * aStopOptions.length)] ?? 10;
  const safeAStop = Math.min(aStop, horizon - 5);

  return {
    ...state,
    monthly,
    rate,
    horizon,
    aStart: 0,
    aStop: safeAStop,
    bStart: safeAStop,
    bStop: horizon,
  };
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<PowerOfTimeState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.power-of-time.label',
    demoId: 'finance',
    advancedKey: 'power-of-time',

    initialState: (ctx) => defaultState(ctx.advanced),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, PowerOfTimeState>[] {
      const rows: PanelRow<FinanceKey, PowerOfTimeState>[] = [
        {
          kind: 'slider',
          id: 'monthly',
          labelKey: 'param.monthly.label',
          min: 50,
          max: 1000,
          step: 50,
          value: state.monthly,
          format: formatMoney,
          onChange: (v) => ({ ...state, monthly: v }),
        },
        {
          kind: 'slider',
          id: 'rate',
          labelKey: 'param.rate.label',
          min: 0.02,
          max: 0.15,
          step: 0.005,
          value: state.rate,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, rate: v }),
        },
        {
          kind: 'slider',
          id: 'horizon',
          labelKey: 'param.horizon.label',
          min: 20,
          max: 60,
          step: 5,
          value: state.horizon,
          format: formatYears,
          onChange: (v) => ({
            ...state,
            horizon: v,
            // Keep bStop clamped to new horizon
            bStop: Math.min(state.bStop, v),
          }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(3, 0, {
          kind: 'slider',
          id: 'aStart',
          labelKey: 'param.a-start.label',
          min: 0,
          max: state.horizon - 1,
          step: 1,
          value: state.aStart,
          format: formatYears,
          onChange: (v) => ({ ...state, aStart: v }),
        });
        rows.splice(4, 0, {
          kind: 'slider',
          id: 'aStop',
          labelKey: 'param.a-stop.label',
          min: 1,
          max: state.horizon,
          step: 1,
          value: state.aStop,
          format: formatYears,
          onChange: (v) => ({ ...state, aStop: v }),
        });
        rows.splice(5, 0, {
          kind: 'slider',
          id: 'bStart',
          labelKey: 'param.b-start.label',
          min: 0,
          max: state.horizon - 1,
          step: 1,
          value: state.bStart,
          format: formatYears,
          onChange: (v) => ({ ...state, bStart: v }),
        });
        rows.splice(6, 0, {
          kind: 'slider',
          id: 'bStop',
          labelKey: 'param.b-stop.label',
          min: 1,
          max: state.horizon,
          step: 1,
          value: state.bStop,
          format: formatYears,
          onChange: (v) => ({ ...state, bStop: v }),
        });
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, PowerOfTimeState> {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): PowerOfTimeState {
      // Pin the classic early-beats-late pose for screenshots.
      return {
        ...state,
        monthly: 200,
        rate: 0.08,
        horizon: 40,
        aStart: 0,
        aStop: 10,
        bStart: 10,
        bStop: 40,
      };
    },

    createRender(ctx, host, _controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Coin icon at the y-axis head
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Line series layer
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      function updateLegend(): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        // Right-anchor labels so they never overlap the rising curves.
        const labelRightX = width - padding.right - 8;
        const swatchLen = 16;
        const swatchGap = 6;
        const legendY = padding.top + 16;

        const items = [
          { cls: 'fin-line-a fin-legend-dot', key: 'series.saver-a.label' as FinanceKey },
          { cls: 'fin-line-b fin-legend-dot', key: 'series.saver-b.label' as FinanceKey },
        ];

        items.forEach((item, i) => {
          const rowY = legendY + i * 18;
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          const lineEl = createSvgEl('line', {
            class: item.cls,
            x1: swatchX1,
            y1: rowY,
            x2: swatchX2,
            y2: rowY,
          });
          legendGroup.appendChild(lineEl);

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = ctx.i18n.t(item.key);
          legendGroup.appendChild(label);
        });
      }

      return {
        update(state: PowerOfTimeState): void {
          const m = powerOfTimeModel(state, ctx.advanced);
          const saverA = m.series.find((s) => s.id === 'saverA');
          const saverB = m.series.find((s) => s.id === 'saverB');
          const aFinal = saverA?.points.at(-1)?.value ?? 0;
          const bFinal = saverB?.points.at(-1)?.value ?? 0;
          const yMax = niceCeil(Math.max(aFinal, bFinal) * 1.05);

          chart.setScales({
            xDomain: [0, state.horizon],
            yDomain: [0, yMax],
          });

          if (saverA) {
            lines.setSeries('saverA', saverA.points, 'fin-line-a');
          }
          if (saverB) {
            lines.setSeries('saverB', saverB.points, 'fin-line-b');
          }

          updateLegend();
          readout.update(m.readout.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
